import { z } from 'zod';
import { AutonomyClass, EffectDecision } from './actions.ts';
import { ActivationId, ActivityId, GateId, parseActivationId } from './activation.ts';
import { SubjectPrecondition } from './approval.ts';
import { EntityRef, Id, IsoDateTime, JsonObject, SemVer, Sha256Hex, TenantId } from './common.ts';
import { Deadline, DeadlineBreach } from './deadline.ts';
import { Delegation } from './delegation.ts';
import { ResourceScope, ScopeDimension } from './entitlement.ts';
import { PlatformError } from './errors.ts';
import { deterministicId, hashCanonical } from './hash.ts';
import { Hold, ParkedResult } from './hold.ts';
import { ClosePolicy } from './obligation.ts';
import { ResolutionSpec, resolutionSpecIssues } from './resolution.ts';
import { EpisodeOutcome, SpawnContinuation, TaskOutcome, TaskState } from './task.ts';

// 26a: `SpawnContinuation` and `CONTINUATION_LIMITS` moved to task.ts (no import cycle); re-exported from the index there.
export { CONTINUATION_LIMITS, SpawnContinuation, type SpawnContinuationInput } from './task.ts';

/**
 * 17a (ADR-015): process composition. A process is code-first logic (`defineWorkflow`) plus declarative metadata
 * (`defineProcess`) bound to one immutable definition version. The workflow run is the authoritative execution
 * state; `ProcessInstance`, `ActivityInstance`, `Case` and timelines are projections rebuilt from run, task and
 * approval events.
 */

/** Process definition name, e.g. `p2p.procure-to-pay`. */
export const ProcessName = z
  .string()
  .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9][a-z0-9-]*)*$/, 'process name must be dotted lowercase kebab case')
  .meta({ id: 'ProcessName' });

export const ProcessDefinitionRef = z
  .object({ name: ProcessName, version: SemVer })
  .meta({ id: 'ProcessDefinitionRef' });
export type ProcessDefinitionRef = z.infer<typeof ProcessDefinitionRef>;

/** The `defineWorkflow` definition a process version is bound to (1:1). */
export const WorkflowRef = z
  .object({ name: z.string().min(1), version: z.string().min(1) })
  .meta({ id: 'WorkflowRef' });
export type WorkflowRef = z.infer<typeof WorkflowRef>;

// -----------------------------------------------------------------------------------------------
// Process definition metadata
// -----------------------------------------------------------------------------------------------

export const ActivityKind = z
  .enum(['agent', 'human', 'service', 'subprocess', 'event', 'timer'])
  .meta({ id: 'ActivityKind' });
export type ActivityKind = z.infer<typeof ActivityKind>;

/** One activity a process may activate. `agent` activities name the capability they invoke. */
export const ActivityDefinition = z
  .object({
    id: ActivityId,
    kind: ActivityKind,
    title: z.string().min(1).max(200).optional(),
    capability: z.string().min(1).max(200).optional(),
    capabilityVersion: z.string().min(1).max(50).optional(),
    /** Gate (approval request) this activity raises or waits on. */
    gateId: GateId.optional(),
    /** For `subprocess`: the child process definition. */
    subprocess: ProcessDefinitionRef.optional(),
    /** True when the activity may be re-entered (loops, revisions); iterations > 1 are refused otherwise. */
    repeatable: z.boolean().default(false),
    /** Owner role shown to operators (human work), pack-declared. */
    ownerRole: z.string().min(1).optional(),
    /**
     * Added by 20a: `input` — the capability and version of this agent activity come from the start input
     * (`$agent`), not from the definition (the platform one-activity process `platform.agent-run`). The binding is
     * resolved at start and pinned like any other (no version fallback).
     */
    capabilityFrom: z.literal('input').optional(),
    /**
     * Added by 21 step 0 (brief 21 §1b.10, ADR-021): the tools (catalog names) activations of this activity may
     * call. Enforced by the tool gateway for spawned tasks and direct `gateway.execute` service calls alike
     * (`tool_out_of_scope`, ledgered). Absent (legacy definitions) = pre-21 behaviour with a `tool_scope_unset`
     * ledger warning.
     */
    tools: z.array(z.string().min(1).max(200)).optional(),
    /** Added by 21 step 0: highest autonomy class an action of this activity may have (above → `tool_out_of_scope`). */
    maxAutonomy: AutonomyClass.optional(),
  })
  .superRefine((a, ctx) => {
    if (
      a.kind === 'agent' &&
      a.capabilityFrom === undefined &&
      (a.capability === undefined || a.capabilityVersion === undefined)
    ) {
      ctx.addIssue({ code: 'custom', message: 'agent activities need capability and capabilityVersion' });
    }
    if (a.kind === 'subprocess' && a.subprocess === undefined) {
      ctx.addIssue({ code: 'custom', message: 'subprocess activities need subprocess { name, version }' });
    }
  })
  .meta({ id: 'ActivityDefinition' });
export type ActivityDefinition = z.infer<typeof ActivityDefinition>;
export type ActivityDefinitionInput = z.input<typeof ActivityDefinition>;

/** An approval gate: requests raised for it have a typed `subjectType` and a pack-resolved route. */
export const GateDefinition = z
  .object({
    id: GateId,
    title: z.string().min(1).max(200).optional(),
    /** Business subject type the gate approves (e.g. `p2p.purchase-requisition`), or `actions`. */
    subjectType: z.string().min(1).max(200),
    /** Key the pack's route resolver maps (with typed policy facts) to stages. */
    routeKey: z.string().min(1).max(200),
  })
  .meta({ id: 'GateDefinition' });
export type GateDefinition = z.infer<typeof GateDefinition>;

/** Event trigger: an inbound event (ADR-018) starts a new process instance. */
export const EventTrigger = z
  .object({
    kind: z.literal('event'),
    source: z.string().min(1),
    type: z.string().min(1),
    /** Create a new case for the instance (true) or require correlation to an existing case (false). */
    createCase: z.boolean().default(true),
    /** JSON pointer into the payload that yields the business key, e.g. `/contractId`. */
    businessKeyPath: z.string().startsWith('/').optional(),
    /** Duplicate trigger rule: one instance per event id, or per business key. */
    dedupeBy: z.enum(['eventId', 'businessKey']).default('eventId'),
    /**
     * With `dedupeBy: 'businessKey'`: one instance per key within this window (fixed windows aligned to the epoch,
     * by `receivedAt`); absent = one instance per key ever. Ignored for `eventId`.
     */
    dedupeWindowSeconds: z.number().int().positive().optional(),
    /**
     * Added by 17c1: how the started instance's input is built. `payload` (default): the event payload without
     * `$`-prefixed keys (platform-reserved); `mapping`: only the keys of `inputMapping`, each a JSON pointer into
     * the payload (absent values are omitted).
     */
    inputFrom: z.enum(['payload', 'mapping']).default('payload'),
    inputMapping: z
      .record(
        z.string().regex(/^[^$]/, 'input keys starting with $ are reserved'),
        z.string().startsWith('/'),
      )
      .optional(),
  })
  .refine((t) => t.inputFrom !== 'mapping' || t.inputMapping !== undefined, {
    message: 'inputFrom: mapping needs inputMapping',
  })
  .meta({ id: 'EventTrigger' });
export type EventTrigger = z.infer<typeof EventTrigger>;

/**
 * Dedupe key of an event-triggered start (ADR-018 §7): the process `startProcess` idempotency key. Two
 * deliveries of one event, or two events with one business key inside the window, yield the same key.
 * Throws `validation_failed` when `dedupeBy: 'businessKey'` and the event has no business key.
 */
export function triggerDedupeKey(
  processName: string,
  trigger: EventTrigger,
  event: { source: string; type: string; eventId: string; businessKey?: string; receivedAt: string },
): string {
  const base = `trigger:${processName}:${event.source}:${event.type}`;
  if (trigger.dedupeBy === 'eventId') return `${base}:id:${event.eventId}`;
  if (event.businessKey === undefined) {
    throw new PlatformError('validation_failed', 'Trigger dedupes by business key but the event has none', {
      details: { processName, source: event.source, type: event.type, eventId: event.eventId },
    });
  }
  if (trigger.dedupeWindowSeconds === undefined) return `${base}:key:${event.businessKey}`;
  const windowMs = trigger.dedupeWindowSeconds * 1000;
  const bucket = Math.floor(Date.parse(event.receivedAt) / windowMs);
  return `${base}:key:${event.businessKey}:w${bucket}`;
}

/**
 * Added by 17c1 (D2): the business key an event trigger dedupes by. `businessKeyPath` (a JSON pointer, RFC 6901)
 * into the payload when set and it yields a string or number; otherwise the event's `correlation.businessKey`.
 * Undefined when neither gives one.
 */
export function triggerBusinessKey(
  trigger: Pick<EventTrigger, 'businessKeyPath'>,
  event: { payload: Record<string, unknown>; correlation?: { businessKey?: string } },
): string | undefined {
  if (trigger.businessKeyPath !== undefined) {
    const value = jsonPointer(event.payload, trigger.businessKeyPath);
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
  }
  return event.correlation?.businessKey;
}

/** Added by 17c1: the value at a JSON pointer (RFC 6901) in `doc`, or undefined. */
export function jsonPointer(doc: unknown, pointer: string): unknown {
  let cursor: unknown = doc;
  for (const raw of pointer.split('/').slice(1)) {
    const key = raw.replaceAll('~1', '/').replaceAll('~0', '~');
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

/**
 * Added by 17c1 (review S2): the input an event-triggered instance starts with (`EventTrigger.inputFrom`).
 * `$`-prefixed payload keys are dropped: the platform reserves them (`$trigger`, `$title`, `$businessKeys`).
 */
export function triggerInput(
  trigger: Pick<EventTrigger, 'inputFrom' | 'inputMapping'>,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  if (trigger.inputFrom === 'mapping') {
    const out: Record<string, unknown> = {};
    for (const [key, pointer] of Object.entries(trigger.inputMapping ?? {})) {
      if (key.startsWith('$')) continue;
      const value = jsonPointer(payload, pointer);
      if (value !== undefined) out[key] = value;
    }
    return out;
  }
  return Object.fromEntries(Object.entries(payload).filter(([key]) => !key.startsWith('$')));
}

/**
 * Added by 17c1 (D2): the process instance id a start with `idempotencyKey` gets in `tenantId` (deterministic).
 * Manual starts and trigger starts (event, timer) use separate namespaces, so a caller-chosen manual
 * `Idempotency-Key` can never address a triggered instance (review S1).
 */
export function processInstanceIdFor(
  tenantId: string,
  idempotencyKey: string,
  origin: 'manual' | 'trigger' = 'manual',
): string {
  return origin === 'manual'
    ? deterministicId(`process:${tenantId}:${idempotencyKey}`)
    : deterministicId(`process-trigger:${tenantId}:${idempotencyKey}`);
}

/** Added by 17c1 (D2): start idempotency key of a timer trigger fire (`timer:<process>@<scheduledAt>`). */
export function timerTriggerKey(processName: string, scheduledAt: string): string {
  return `timer:${processName}@${scheduledAt}`;
}

/** Timer trigger: one instance per fire time (`<process>@<scheduledAt>` dedupes double fires). */
export const TimerTrigger = z
  .object({
    kind: z.literal('timer'),
    /** Five-field cron expression, evaluated in `timezone`. */
    cron: z.string().min(1),
    timezone: z.string().min(1).default('UTC'),
  })
  .meta({ id: 'TimerTrigger' });
export type TimerTrigger = z.infer<typeof TimerTrigger>;

export const ManualTrigger = z
  .object({
    kind: z.literal('manual'),
    /** Permission the starter needs in addition to `case.act` on the requested scope. */
    permission: z.string().min(1).optional(),
  })
  .meta({ id: 'ManualTrigger' });
export type ManualTrigger = z.infer<typeof ManualTrigger>;

export const ProcessTrigger = z
  .discriminatedUnion('kind', [ManualTrigger, EventTrigger, TimerTrigger])
  .meta({ id: 'ProcessTrigger' });
export type ProcessTrigger = z.infer<typeof ProcessTrigger>;

/** A capability the process needs, with the exact contract version (no fallback, ADR-020). */
export const CapabilityRequirement = z
  .object({ capability: z.string().min(1).max(200), version: z.string().min(1).max(50) })
  .meta({ id: 'CapabilityRequirement' });
export type CapabilityRequirement = z.infer<typeof CapabilityRequirement>;

/**
 * Execution budgets of one process instance. Values are TBD pending `17x-engine-benchmark`; every field is
 * optional. Since 17c1 (D4) `ProcessOrchestrator.spawnTask` and work-item opens enforce `maxActivations` (child
 * activations: spawned tasks and work items), `maxConcurrentActivities` (non-terminal children), `maxFanOut`
 * (branches of one `fanOut.group`) and `deadlineSeconds` (no new child after `createdAt + deadlineSeconds`; child
 * deadlines are clamped to it), refusing with `limit_exceeded` before anything is created. The engines enforce
 * `maxHistorySteps` / `maxStepPayloadBytes` through their own budgets (`WORKFLOW_ENGINE_BUDGETS`).
 */
export const ProcessBudgets = z
  .object({
    maxActivations: z.number().int().positive().optional(),
    maxConcurrentActivities: z.number().int().positive().optional(),
    maxFanOut: z.number().int().positive().optional(),
    maxHistorySteps: z.number().int().positive().optional(),
    maxStepPayloadBytes: z.number().int().positive().optional(),
    deadlineSeconds: z.number().int().positive().optional(),
  })
  .meta({ id: 'ProcessBudgets' });
export type ProcessBudgets = z.infer<typeof ProcessBudgets>;

/** Added by 17c1 (D4): reason of a `limit_exceeded` budget refusal (`details.reason`). */
export const ProcessBudgetReason = z
  .enum(['max_activations', 'max_concurrent_activities', 'max_fan_out', 'deadline'])
  .meta({ id: 'ProcessBudgetReason' });
export type ProcessBudgetReason = z.infer<typeof ProcessBudgetReason>;

/** Added by 17c1 (D4): what an instance has used of its budgets (shown with `ProcessInstance.budgets`). */
export const ProcessBudgetUsage = z
  .object({
    /** Child activations created (spawned tasks and work items, one per activation id). */
    activations: z.number().int().nonnegative(),
    /** Children not yet terminal (tasks and open work items). */
    open: z.number().int().nonnegative(),
    /** Branches per fan-out group. */
    fanOut: z.record(z.string(), z.number().int().nonnegative()).default({}),
    /** `createdAt + budgets.deadlineSeconds`, when a deadline is set. */
    deadline: IsoDateTime.optional(),
  })
  .meta({ id: 'ProcessBudgetUsage' });
export type ProcessBudgetUsage = z.infer<typeof ProcessBudgetUsage>;

/** Optional presentation hints; the generic shell never requires a stage rail. */
export const ProcessPresentation = z
  .object({
    stageGroups: z
      .array(
        z.object({
          id: z.string().min(1),
          title: z.string().min(1),
          activityIds: z.array(ActivityId).min(1),
        }),
      )
      .optional(),
  })
  .meta({ id: 'ProcessPresentation' });
export type ProcessPresentation = z.infer<typeof ProcessPresentation>;

/** Declarative metadata of one immutable process definition version (`defineProcess`). */
export const ProcessDefinitionMeta = z
  .object({
    name: ProcessName,
    version: SemVer,
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(4000).optional(),
    workflow: WorkflowRef,
    activities: z.array(ActivityDefinition).min(1),
    gates: z.array(GateDefinition).default([]),
    triggers: z.array(ProcessTrigger).default([{ kind: 'manual' }]),
    requiredCapabilities: z.array(CapabilityRequirement).default([]),
    /** Pack-declared scope dimensions every instance and case of this process carries. */
    scopeDimensions: z.array(ScopeDimension).default([]),
    /** Entity/artefact types the process links to its case (e.g. `erp.purchase-order`). */
    artefactTypes: z.array(z.string().min(1)).default([]),
    /** Reference to the pack's configuration schema (e.g. `schemas/p2p-config.ts#P2PConfig`). */
    configSchemaRef: z.string().min(1).optional(),
    presentation: ProcessPresentation.prefault({}),
    /** Pack-owned eval suite ids (registration with the eval catalog is not in 17a). */
    evaluations: z.array(z.string().min(1)).default([]),
    budgets: ProcessBudgets.prefault({}),
    /**
     * Added by 21 step 0 (§1b.6): closure of a succeeded instance's case with open obligations. Absent =
     * `require_obligations_met` (the case stays `in_progress`, "awaiting closure").
     */
    closePolicy: ClosePolicy.optional(),
    /**
     * Added by 21 step 0: child process definitions this process may start with `startChild` (each pinned like the
     * parent: rel-01 holds for children). Absent = none besides `subprocess` activities' own refs.
     */
    children: z.array(ProcessDefinitionRef).optional(),
    /**
     * Added by 21 step 0: reference to the start-input Zod schema the pack registers (`GET /processes/{name}` serves
     * it as `inputSchema`, JSON Schema with `x-classification` / `x-ui`).
     */
    inputSchemaRef: z.string().min(1).max(300).optional(),
    /**
     * Added by 26a (brief 26 §3.1): when a case of this process counts as resolved. Snapshotted into
     * `ReleasePin.resolution` at start. Absent = `resolution_undefined` (warn mode: registered with a warning and
     * evaluated as legacy; strict mode: refused). Adding it changes `metaHash`, so bump the process version.
     */
    resolution: ResolutionSpec.optional(),
  })
  .meta({ id: 'ProcessDefinitionMeta' });
export type ProcessDefinitionMeta = z.infer<typeof ProcessDefinitionMeta>;
export type ProcessDefinitionMetaInput = z.input<typeof ProcessDefinitionMeta>;

/**
 * Added by 21a review (item 17b): process names a definition may not take, because they are static segments of the
 * process routes (`GET /processes/definitions`, `/processes/instances/{id}`) and would shadow `/processes/{name}`.
 */
export const RESERVED_PROCESS_NAMES: readonly string[] = ['definitions', 'instances'];

/** Problems in a parsed `ProcessDefinitionMeta` that Zod shapes alone cannot express (empty = valid). */
export function processMetaIssues(meta: ProcessDefinitionMeta): string[] {
  const issues: string[] = [];
  if (RESERVED_PROCESS_NAMES.includes(meta.name)) {
    issues.push(`process name "${meta.name}" is reserved by the /processes routes`);
  }
  const dup = (values: string[]) => values.filter((v, i) => values.indexOf(v) !== i);
  for (const id of new Set(dup(meta.activities.map((a) => a.id))))
    issues.push(`duplicate activity id "${id}"`);
  for (const id of new Set(dup(meta.gates.map((g) => g.id)))) issues.push(`duplicate gate id "${id}"`);
  for (const d of new Set(dup(meta.scopeDimensions))) issues.push(`duplicate scope dimension "${d}"`);
  const gateIds = new Set(meta.gates.map((g) => g.id));
  const activityIds = new Set(meta.activities.map((a) => a.id));
  const declared = new Set(meta.requiredCapabilities.map((c) => `${c.capability}@${c.version}`));
  for (const a of meta.activities) {
    if (a.gateId !== undefined && !gateIds.has(a.gateId)) {
      issues.push(`activity "${a.id}" references undeclared gate "${a.gateId}"`);
    }
    if (a.capability !== undefined) {
      const key = `${a.capability}@${a.capabilityVersion ?? '?'}`;
      if (!declared.has(key)) issues.push(`activity "${a.id}" uses undeclared capability ${key}`);
    }
  }
  const referencedGates = new Set(meta.activities.flatMap((a) => (a.gateId === undefined ? [] : [a.gateId])));
  for (const g of meta.gates) {
    if (!referencedGates.has(g.id)) issues.push(`gate "${g.id}" is referenced by no activity`);
  }
  // 21 step 0: a declared `children` list must cover every subprocess activity's child definition.
  if (meta.children !== undefined) {
    const children = new Set(meta.children.map((c) => `${c.name}@${c.version}`));
    for (const a of meta.activities) {
      if (a.subprocess !== undefined && !children.has(`${a.subprocess.name}@${a.subprocess.version}`)) {
        issues.push(
          `activity "${a.id}" starts ${a.subprocess.name}@${a.subprocess.version}, which children does not list`,
        );
      }
    }
  }
  for (const a of meta.activities) {
    for (const t of new Set(dup(a.tools ?? []))) issues.push(`activity "${a.id}" lists tool "${t}" twice`);
  }
  for (const group of meta.presentation.stageGroups ?? []) {
    for (const id of group.activityIds) {
      if (!activityIds.has(id)) issues.push(`stage group "${group.id}" references unknown activity "${id}"`);
    }
  }
  // 26a (brief 26 §3.1): `ownerRole` is required unless the condition is `activity_completed`.
  if (meta.resolution !== undefined) issues.push(...resolutionSpecIssues(meta.resolution));
  return issues;
}

/** Normal-form version of `processMetaHash`; bump only with a migration note in ADR-020. */
export const PROCESS_META_HASH_FORM = 1;

/**
 * `ReleasePin.metaHash`: `hashCanonical({ form, meta })` over the **authored input** (before Zod defaults), so a
 * later contract version that adds a defaulted field does not change the hash of an existing registration.
 * Key order and `undefined` members do not matter (canonical JSON).
 */
export function processMetaHash(authored: ProcessDefinitionMetaInput): string {
  return hashCanonical({ form: PROCESS_META_HASH_FORM, meta: authored });
}

// -----------------------------------------------------------------------------------------------
// Bindings and release pins (ADR-020)
// -----------------------------------------------------------------------------------------------

/** Capability resolution persisted at admission. Never re-resolved to another version for that run. */
export const CapabilityBinding = z
  .object({
    capability: z.string().min(1).max(200),
    version: z.string().min(1).max(50),
    agentName: z.string().min(1),
    agentVersion: SemVer,
    resolvedAt: IsoDateTime,
  })
  .meta({ id: 'CapabilityBinding' });
export type CapabilityBinding = z.infer<typeof CapabilityBinding>;

/**
 * Everything an instance pins at start: definition, workflow version, metadata hash, agent bindings, schema
 * and prompt versions and the policy/configuration versions. Old runs finish on their pin; kill switches and
 * revoked authority still apply (they are current controls, not pinned).
 */
export const ReleasePin = z
  .object({
    process: ProcessDefinitionRef,
    workflow: WorkflowRef,
    /** `processMetaHash(authored meta)` of the registered version. */
    metaHash: Sha256Hex,
    bindings: z.array(CapabilityBinding).default([]),
    policyVersion: z.string().min(1),
    configVersion: z.string().min(1).optional(),
    /** Schema ref → content hash (inputs/outputs of bound capabilities, pack config). */
    schemaHashes: z.record(z.string(), Sha256Hex).default({}),
    /** Agent name → prompt version. */
    promptVersions: z.record(z.string(), z.string()).default({}),
    /** Tool versions the instance's activities may call (catalog `name@version` at start). */
    tools: z.array(z.object({ name: z.string().min(1), version: z.string().min(1) })).default([]),
    /**
     * Added by 26a (brief 26 §3.1): the resolution snapshotted at start — `ProcessDefinitionMeta.resolution`, or for
     * `platform.agent-run` the serving agent's `spec.outcome.resolution`. Never changes for the instance's life;
     * absent = legacy instance (evaluated as "process succeeded").
     */
    resolution: ResolutionSpec.optional(),
  })
  .meta({ id: 'ReleasePin' });
export type ReleasePin = z.infer<typeof ReleasePin>;

// -----------------------------------------------------------------------------------------------
// Task invocation
// -----------------------------------------------------------------------------------------------

/**
 * `ProcessOrchestrator.spawnTask` input. Idempotent by `(processInstanceId, activationId, attempt)`.
 * `delegation` is a **request**: the orchestrator narrows it against the persisted parent delegation
 * (`ProcessInstance.delegation` or the parent task's) and rejects widening; it is never trusted as given.
 * A failed child is normally re-run as a **new iteration** (a revision: `review#2`); `attempt` > 0 is a
 * task-level retry of the same activation (infrastructure failure, no new business input).
 */
export const SpawnTaskRequest = z
  .object({
    tenantId: TenantId,
    processInstanceId: Id,
    caseId: Id.optional(),
    parentTaskId: Id.optional(),
    activationId: ActivationId,
    capability: z.string().min(1).max(200),
    version: z.string().min(1).max(50),
    objective: z.string().min(1).max(4000),
    input: JsonObject.default({}),
    scope: ResourceScope.default({}),
    delegation: Delegation,
    /** Task-level retry counter of this activation (0 = first task). */
    attempt: z.number().int().nonnegative().default(0),
    deadline: IsoDateTime.optional(),
    /**
     * Added by 17b2: business subjects every effect of the spawned task depends on (ADR-016 §2). The orchestrator
     * persists them with the spawn; the governed workflow attaches them to each non-read `ActionIntent` as
     * `subjectPreconditions`, and the gateway path refuses execution while they do not hold. Set by process code,
     * never by model output.
     */
    subjectPreconditions: z.array(SubjectPrecondition).optional(),
    /**
     * Added by 17c1 (D6): this activation is branch `index` of fan-out group `group` (ADR-015 §3). Persisted with the
     * spawn and projected on the activity; `budgets.maxFanOut` bounds the branches of one group.
     */
    fanOut: z.object({ group: z.string().min(1).max(200), index: z.number().int().nonnegative() }).optional(),
    /**
     * Added by 20a: task-request fields a one-activity case passes through to its agent task (absent: the task gets
     * no entity refs or artefacts, `requestedActionScope` = the narrowed delegation's action scopes, `channel`
     * `process`, `domain` the agent's domain). Part of the spawn request hash only when set.
     */
    entityRefs: z.array(EntityRef).optional(),
    artifactRefs: z.array(z.string().min(1)).optional(),
    requestedActionScope: z.array(z.string().min(1)).optional(),
    channel: z.string().min(1).max(64).optional(),
    domain: z.string().min(1).max(100).optional(),
    /**
     * Added by 21 step 0 (§1b.10): narrows the task's tools further. Must be a subset of the activity's `tools`
     * (validated by the orchestrator, `tool_out_of_scope` otherwise; never trusted). Part of the spawn request hash
     * only when set, so pre-21 instances replay unchanged.
     */
    toolScope: z.array(z.string().min(1).max(200)).optional(),
    /**
     * Added by 26a (brief 26 §3.2b, §4.1): what continuation run `run#<round>` is given — prior outcomes, the answers
     * to the gaps it left and the verified effects so far. A field beside `input`, never inside it (the pack's input
     * schema is untouched). Round 1 has none. Persisted on the task row (`core_tasks.tasks.continuation`).
     */
    continuation: SpawnContinuation.optional(),
  })
  .meta({ id: 'SpawnTaskRequest' });
export type SpawnTaskRequest = z.infer<typeof SpawnTaskRequest>;
export type SpawnTaskRequestInput = z.input<typeof SpawnTaskRequest>;

/**
 * Added by 17c1 (D5): largest `TaskCompletionSignal.output` carried inline (canonical JSON bytes); a larger output
 * travels as `outputRef` (`task:<taskId>/proposal`) and is read from the child's proposal.
 */
export const TASK_COMPLETION_OUTPUT_MAX_BYTES = 64 * 1024;

/**
 * Payload of signal `task:<activationId>`: the spawned task reached a terminal state. Since 17c1 (D5) `output` is
 * the child's validated `Proposal.structuredOutput` (bounded by `TASK_COMPLETION_OUTPUT_MAX_BYTES`, else `outputRef`).
 */
export const TaskCompletionSignal = z
  .object({
    taskId: Id,
    activationId: ActivationId,
    state: TaskState.refine((s) => ['succeeded', 'failed', 'cancelled', 'expired'].includes(s), {
      message: 'completion state must be terminal',
    }),
    outcome: TaskOutcome.optional(),
    /** Validated output (capability output schema) or a reference to it when large. */
    output: JsonObject.optional(),
    outputRef: z.string().min(1).optional(),
    summary: z.string().optional(),
    /** Added by 20a: how the episode ended (`succeeded`, `inconclusive`, `error`, `cancelled`) and why. */
    episodeOutcome: EpisodeOutcome.optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'TaskCompletionSignal' });
export type TaskCompletionSignal = z.infer<typeof TaskCompletionSignal>;

// -----------------------------------------------------------------------------------------------
// Process and activity instances (projections of the run)
// -----------------------------------------------------------------------------------------------

/** Name of the memoized step that records `ProcessInstance.pin` at start. */
export const PROCESS_PIN_STEP = 'process/pin';

/** Instance states. Added by 21 step 0: `on_hold` — a boundary hold for a business reason (`Hold`, §1b.3). */
export const ProcessInstanceState = z
  .enum(['running', 'waiting', 'on_hold', 'cancelling', 'succeeded', 'failed', 'cancelled'])
  .meta({ id: 'ProcessInstanceState' });
export type ProcessInstanceState = z.infer<typeof ProcessInstanceState>;

/**
 * Legal process-instance transitions. `cancelling` holds until every child is terminal and every uncertain
 * business operation is reconciled; only then `cancelled` (with outcome `partially_completed` or
 * `outcome_unknown` when effects remain).
 */
/**
 * Added by 17c1: outcome of a process instance. `TaskOutcome` plus `cancelled` (a clean cancellation: every child
 * terminal and no business operation left an effect); a cancellation with effects is `partially_completed`.
 */
export const ProcessOutcome = z.enum([...TaskOutcome.options, 'cancelled']).meta({ id: 'ProcessOutcome' });
export type ProcessOutcome = z.infer<typeof ProcessOutcome>;

export const PROCESS_INSTANCE_TRANSITIONS: Readonly<
  Record<ProcessInstanceState, readonly ProcessInstanceState[]>
> = {
  running: ['waiting', 'on_hold', 'cancelling', 'succeeded', 'failed'],
  waiting: ['running', 'on_hold', 'cancelling', 'failed'],
  on_hold: ['running', 'waiting', 'cancelling', 'failed'],
  cancelling: ['cancelled', 'failed'],
  succeeded: [],
  failed: [],
  cancelled: [],
};

export const ProcessInstance = z
  .object({
    processInstanceId: Id,
    tenantId: TenantId,
    caseId: Id.optional(),
    definition: ProcessDefinitionRef,
    /** Authoritative execution identity: the workflow run. */
    runId: Id,
    /**
     * Recorded by the run's first memoized step (`PROCESS_PIN_STEP`) and never changed; replay returns the same
     * pin. `spawnTask` resolves capabilities from `pin.bindings` only.
     */
    pin: ReleasePin,
    /**
     * Server-held root delegation, set at start from the initiator's verified entitlements (or the trigger's
     * configured authority). Every `spawnTask` narrows against it.
     */
    delegation: Delegation,
    state: ProcessInstanceState,
    outcome: ProcessOutcome.optional(),
    scope: ResourceScope.default({}),
    trigger: z.object({ kind: z.enum(['manual', 'event', 'timer']), ref: z.string().optional() }),
    startedBy: z.string().min(1),
    parentProcessInstanceId: Id.optional(),
    parentActivationId: ActivationId.optional(),
    inputRef: z.string().optional(),
    resultRef: z.string().optional(),
    /** Added by 17c1 (D8): the handler's result, stored by the run's completion step. */
    result: JsonObject.optional(),
    /** Added by 17c1 (D8/D9): why the instance failed or was cancelled (`<code>: <message>`). */
    reason: z.string().optional(),
    budgets: ProcessBudgets.prefault({}),
    /** Added by 17c1 (D4): use of `budgets` so far. */
    budgetUsage: ProcessBudgetUsage.optional(),
    /**
     * Added by 26a (brief 26 §3.1): the pinned resolution (= `pin.resolution`, surfaced for readers). Absent = legacy
     * instance.
     */
    resolution: ResolutionSpec.optional(),
    /**
     * Added by 21 step 0 (§1b.7): the configuration version resolved at start (`profile > tenant override > pack
     * default`), snapshotted for the instance's life. `null` for instances started before 21.
     */
    configVersion: z.string().min(1).nullable().optional(),
    /** Added by 21 step 0: the open hold while `state` is `on_hold` (or an `activation`-scoped hold). */
    hold: Hold.optional(),
    /** Added by 21 step 0: results parked while held, delivered after resume (§1b.3). */
    parked: z.array(ParkedResult).optional(),
    /** Projection revision; every applied command increments it. */
    revision: z.number().int().nonnegative(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    completedAt: IsoDateTime.optional(),
  })
  .meta({ id: 'ProcessInstance' });
export type ProcessInstance = z.infer<typeof ProcessInstance>;

/**
 * Activity states. Added by 20a: `awaiting_input` and `awaiting_approval` (non-terminal) — an `agent` activity whose
 * task waits for clarification answers or for a (task-level) approval decision. Added by 21 step 0: `skipped`
 * (terminal) with an audited reason (`ActivityInstance.skip`, `SkipReason`; §1b.2).
 */
export const ActivityState = z
  .enum([
    'pending',
    'running',
    'waiting',
    'awaiting_input',
    'awaiting_approval',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
    'skipped',
  ])
  .meta({ id: 'ActivityState' });
export type ActivityState = z.infer<typeof ActivityState>;

/** Added by 20a: activity states after which the activation does not change again (except `superseded`). */
export const ACTIVITY_TERMINAL_STATES: readonly ActivityState[] = [
  'succeeded',
  'failed',
  'cancelled',
  'superseded',
  'skipped',
];

/**
 * Added by 21a (§1, §1b.2): activity states a stage rail / progress count treats as done for this case — `skipped`
 * (any reason) counts like `succeeded` and is rendered distinctly.
 */
export const ACTIVITY_DONE_FOR_CASE_STATES: readonly ActivityState[] = ['succeeded', 'skipped'];

/** Added by 21a: true when `state` counts as done for the case (`succeeded` or `skipped`). */
export function isActivityDoneForCase(state: ActivityState): boolean {
  return ACTIVITY_DONE_FOR_CASE_STATES.includes(state);
}

/** Legal activity transitions. `superseded` = a later iteration replaced this one (revision/return). */
export const ACTIVITY_TRANSITIONS: Readonly<Record<ActivityState, readonly ActivityState[]>> = {
  pending: [
    'running',
    'waiting',
    'awaiting_input',
    'awaiting_approval',
    'cancelled',
    'superseded',
    'skipped',
  ],
  running: [
    'waiting',
    'awaiting_input',
    'awaiting_approval',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
    'skipped',
  ],
  waiting: [
    'running',
    'awaiting_input',
    'awaiting_approval',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
    'skipped',
  ],
  awaiting_input: [
    'running',
    'awaiting_approval',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
    'skipped',
  ],
  awaiting_approval: [
    'running',
    'awaiting_input',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
    'skipped',
  ],
  succeeded: ['superseded'],
  failed: ['superseded'],
  cancelled: [],
  superseded: [],
  skipped: [],
};

/**
 * Added by 21 step 0 (§1b.2): why an activation is `skipped`.
 * - `branch_not_taken`: set by the orchestrator on the declared activity ids of a `branch` spawn or route not
 *   selected; carries `routeDecision`.
 * - `not_applicable`: a deliberate bypass of an applicable activity; needs `by` (actor principal), `justification`
 *   and, when the activity has a `gateId`, `gateDecisionId`.
 * - `superseded_route`: spawns cancelled by `join: 'any'` + `cancelOthers`, and re-routing after a hold.
 */
export const SkipReason = z
  .enum(['branch_not_taken', 'not_applicable', 'superseded_route'])
  .meta({ id: 'SkipReason' });
export type SkipReason = z.infer<typeof SkipReason>;

/** The audited record of a skip (on the activity and its timeline entry). */
export const ActivitySkip = z
  .object({
    reason: SkipReason,
    by: z.string().min(1).optional(),
    justification: z.string().min(1).max(2000).optional(),
    gateDecisionId: Id.optional(),
    /** `branch_not_taken` / `superseded_route`: which route won and why (pack-supplied, deterministic). */
    routeDecision: z
      .object({ selected: z.array(z.string().min(1)), rule: z.string().min(1).max(500).optional() })
      .optional(),
  })
  .superRefine((k, ctx) => {
    if (k.reason === 'not_applicable' && (k.by === undefined || k.justification === undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'not_applicable needs an actor (by) and a justification',
        path: ['justification'],
      });
    }
    if (k.reason === 'branch_not_taken' && k.routeDecision === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'branch_not_taken carries the route decision',
        path: ['routeDecision'],
      });
    }
  })
  .meta({ id: 'ActivitySkip' });
export type ActivitySkip = z.infer<typeof ActivitySkip>;

/**
 * Input of `ProcessOrchestrator.skip` (21a): the `not_applicable` variant only — a deliberate bypass by an actor
 * (`by`) with a `justification` and, for an activity with `gateId`, the `gateDecisionId` (checked by the
 * orchestrator against the definition). `branch_not_taken` and `superseded_route` are set by the orchestrator itself
 * (branch join, re-route); a pack call with them does not parse and is refused (`validation_failed`).
 */
export const SkipRequest = z
  .strictObject({
    reason: z.literal('not_applicable'),
    by: z.string().min(1),
    justification: z.string().min(1).max(2000),
    gateDecisionId: Id.optional(),
  })
  .meta({ id: 'SkipRequest' });
export type SkipRequest = z.infer<typeof SkipRequest>;

export function isLegalActivityTransition(from: ActivityState, to: ActivityState): boolean {
  return ACTIVITY_TRANSITIONS[from].includes(to);
}

/** What a `waiting` activity waits for (shown to operators as the waiting reason). */
export const WaitingFor = z
  .object({
    kind: z.enum(['task', 'approval', 'event', 'timer', 'human', 'subprocess']),
    /** Task id, approval request id, event type, child process instance id … */
    ref: z.string().optional(),
    /** Engine signal name(s) the run waits on. */
    signals: z.array(z.string().min(1)).default([]),
    until: IsoDateTime.optional(),
  })
  .meta({ id: 'WaitingFor' });
export type WaitingFor = z.infer<typeof WaitingFor>;

/** One activation of an activity. Retries increment `attempt`; revisions create a new iteration. */
export const ActivityInstance = z
  .object({
    processInstanceId: Id,
    activityId: ActivityId,
    activationId: ActivationId,
    iteration: z.number().int().positive(),
    kind: ActivityKind,
    state: ActivityState,
    attempt: z.number().int().nonnegative().default(0),
    inputRef: z.string().optional(),
    outputRef: z.string().optional(),
    taskId: Id.optional(),
    approvalRequestId: Id.optional(),
    operationId: Id.optional(),
    childProcessInstanceId: Id.optional(),
    /** Added by 17c1 (D3): the work item of a gate-less human activity. */
    workItemId: Id.optional(),
    binding: CapabilityBinding.optional(),
    waitingFor: WaitingFor.optional(),
    supersededBy: ActivationId.optional(),
    /** Set when the activation is one branch of a fan-out (ADR-015 §7): group id and 0-based index. */
    fanOut: z.object({ group: z.string().min(1), index: z.number().int().nonnegative() }).optional(),
    error: z.object({ code: z.string(), message: z.string() }).optional(),
    /** Added by 20a: an `agent` activity's episode outcome (set when its task is terminal). */
    outcome: EpisodeOutcome.optional(),
    /**
     * Added by 20a: after a non-succeeded activation, whether the process went on (`true`, the pack chose to proceed)
     * or stopped (`false`); absent when the process code did not say (`ProcessOrchestrator.noteActivityOutcome`).
     */
    processContinued: z.boolean().optional(),
    /** Added by 21 step 0: set when `state` is `skipped` (§1b.2). */
    skip: ActivitySkip.optional(),
    /** Added by 21 step 0: the activation's business deadline (its engine wait carries it as timeout). */
    deadline: Deadline.optional(),
    /** Added by 21 step 0: set once the deadline passed (`whileHeld` when breached during a hold). */
    deadlineBreach: DeadlineBreach.optional(),
    startedAt: IsoDateTime.optional(),
    finishedAt: IsoDateTime.optional(),
  })
  .superRefine((a, ctx) => {
    if (!ActivationId.safeParse(a.activationId).success) return;
    const { activityId, iteration } = parseActivationId(a.activationId);
    if (activityId !== a.activityId || iteration !== a.iteration) {
      ctx.addIssue({ code: 'custom', message: 'activationId must equal <activityId>#<iteration>' });
    }
  })
  .meta({ id: 'ActivityInstance' });
export type ActivityInstance = z.infer<typeof ActivityInstance>;

// -----------------------------------------------------------------------------------------------
// Events (built-in types, registered in EVENT_SCHEMAS)
// -----------------------------------------------------------------------------------------------

export const PROCESS_EVENT_TYPES = {
  instanceStarted: 'process.instance.started',
  instanceStateChanged: 'process.instance.state.changed',
  activityChanged: 'process.activity.changed',
  /** Added by 21 step 0. */
  childStarted: 'process.child.started',
  /** Added by 21 step 0. */
  childCompleted: 'process.child.completed',
} as const;

/** `process.instance.started`. */
export const ProcessInstanceStartedData = z
  .object({
    processInstanceId: Id,
    tenantId: TenantId,
    caseId: Id.optional(),
    definition: ProcessDefinitionRef,
    runId: Id,
    trigger: z.enum(['manual', 'event', 'timer']),
    startedBy: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'ProcessInstanceStartedData' });
export type ProcessInstanceStartedData = z.infer<typeof ProcessInstanceStartedData>;

/** `process.instance.state.changed`. `commandId` is stable per run step so projections apply it once. */
export const ProcessInstanceStateChangedData = z
  .object({
    processInstanceId: Id,
    caseId: Id.optional(),
    from: ProcessInstanceState,
    to: ProcessInstanceState,
    outcome: ProcessOutcome.optional(),
    reason: z.string().optional(),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'ProcessInstanceStateChangedData' });
export type ProcessInstanceStateChangedData = z.infer<typeof ProcessInstanceStateChangedData>;

/** `process.activity.changed`: an activation was created or changed state. */
export const ProcessActivityChangedData = z
  .object({
    processInstanceId: Id,
    caseId: Id.optional(),
    activationId: ActivationId,
    activityId: ActivityId,
    iteration: z.number().int().positive(),
    kind: ActivityKind,
    from: ActivityState.optional(),
    to: ActivityState,
    taskId: Id.optional(),
    approvalRequestId: Id.optional(),
    operationId: Id.optional(),
    waitingFor: WaitingFor.optional(),
    /** Added by 17c1 (D6). */
    fanOut: z.object({ group: z.string().min(1), index: z.number().int().nonnegative() }).optional(),
    /** Added by 17c1 (D3): the work item of a gate-less human activity. */
    workItemId: Id.optional(),
    /** Added by 20a: the activity's error (`reason` of a failed agent episode). */
    error: z.object({ code: z.string(), message: z.string() }).optional(),
    /** Added by 20a: the episode outcome of an agent activity. */
    outcome: EpisodeOutcome.optional(),
    /** Added by 20a: set only on the `noteActivityOutcome` event (process continued or stopped). */
    processContinued: z.boolean().optional(),
    /** Added by 21 step 0: the skip record when `to` is `skipped`. */
    skip: ActivitySkip.optional(),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'ProcessActivityChangedData' });
export type ProcessActivityChangedData = z.infer<typeof ProcessActivityChangedData>;

/** Stable command id for a projection update emitted by a run step: `<runId>:<stepName>`. */
export function projectionCommandId(runId: string, stepName: string): string {
  return `${runId}:${stepName}`;
}

// -----------------------------------------------------------------------------------------------
// Child processes (added by 21 step 0, brief 21 §1; ADR-021)
// -----------------------------------------------------------------------------------------------

/** Case link relation of a child process's case to its parent case (and the reverse `parent`). */
export const CASE_RELATION_CHILD = 'child';
export const CASE_RELATION_PARENT = 'parent';

/**
 * Options of `ProcessOrchestrator.startChild`. `cascade`: on parent cancel, `cancel` cancels the child (its
 * receipts decided per entry with `cancelChild({ effects })`), `detach` leaves it running. The child is pinned like
 * the parent (same release rules, rel-01).
 */
export const StartChildOptions = z
  .object({
    cascade: z.enum(['cancel', 'detach']).default('cancel'),
    /** Link the child case to the parent case (`relation: 'child'`); default true. */
    link: z.boolean().default(true),
    title: z.string().min(1).max(300).optional(),
    businessKeys: z.array(z.string().min(1).max(255)).max(50).optional(),
  })
  .meta({ id: 'StartChildOptions' });
export type StartChildOptions = z.infer<typeof StartChildOptions>;
export type StartChildOptionsInput = z.input<typeof StartChildOptions>;

/** Signal name that delivers a child process's outcome to the parent run: `child:<activationId>`. */
export function childSignalName(activationId: string): string {
  parseActivationId(activationId);
  return `child:${activationId}`;
}

/** Payload of signal `child:<activationId>`: the child instance reached a terminal state. */
export const ChildCompletionSignal = z
  .object({
    childProcessInstanceId: Id,
    activationId: ActivationId,
    state: z.enum(['succeeded', 'failed', 'cancelled']),
    outcome: ProcessOutcome.optional(),
    result: JsonObject.optional(),
    reason: z.string().optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'ChildCompletionSignal' });
export type ChildCompletionSignal = z.infer<typeof ChildCompletionSignal>;

/** Input of `ProcessOrchestrator.cancelChild`: one decision per completed receipt of the child (§1b.4). */
export const CancelChildRequest = z
  .object({ reason: z.string().min(1).max(2000), effects: z.array(EffectDecision) })
  .meta({ id: 'CancelChildRequest' });
export type CancelChildRequest = z.infer<typeof CancelChildRequest>;

/** `process.child.started`. */
export const ProcessChildStartedData = z
  .object({
    processInstanceId: Id,
    caseId: Id.optional(),
    activationId: ActivationId,
    child: ProcessDefinitionRef,
    childProcessInstanceId: Id,
    childCaseId: Id.optional(),
    cascade: z.enum(['cancel', 'detach']),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'ProcessChildStartedData' });
export type ProcessChildStartedData = z.infer<typeof ProcessChildStartedData>;

/** `process.child.completed`. */
export const ProcessChildCompletedData = z
  .object({
    processInstanceId: Id,
    caseId: Id.optional(),
    activationId: ActivationId,
    childProcessInstanceId: Id,
    state: z.enum(['succeeded', 'failed', 'cancelled']),
    outcome: ProcessOutcome.optional(),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'ProcessChildCompletedData' });
export type ProcessChildCompletedData = z.infer<typeof ProcessChildCompletedData>;
